import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import ChartContainer from "@client.components/ChartContainer";
import { getTotalMeetingsCostAsync, makeTotalMeetingsCost } from "../reducer";
import MeetingTypes from "@client.enums/meetingTypes";
import useAvailableColors from "@client.hooks/useAvailableColors";
import FormatHelper from "@client.utils/FormatHelper";
import CustomTooltip from "../CustomTooltip";
import MeetingTypeColorIndexes from "@client.enums/meetingTypeColorIndexes";
import "./TotalMeetingsCost.scss";
import { filterPropTypes } from "../utils";
import useModal from "@client.hooks/useModal";
import MeetingDetailsContent from "../ChartWithDetailedTooltip/MeetingDetailsContent";
import { getTotalMeetingCostDetailedInformation } from "../consts";
import { makeProfile } from "@client.pages/Login/reducer";
import { useNavigate } from "react-router-dom";
import Pages from "@client.enums/pages";
import CustomBarShape from "./CustomBarShape";

const getUniqueYAxisProps = (data) => {
  let availableYAxis = data.reduce((acc, v) => {
    const totalValue = (v.external || 0) + (v.recurring || 0) + (v.unique || 0);
    acc.push(totalValue);
    return acc;
  }, []);
  const maxValue = Math.max(...availableYAxis);
  const numberOfIntervals = 5;
  const intervalSize = Math.ceil(maxValue / numberOfIntervals);

  let uniqueYAxis = [];
  for (let i = 0; i <= numberOfIntervals; i++) {
    uniqueYAxis.push(i * intervalSize);
  }

  return {
    domain: [0, uniqueYAxis[numberOfIntervals]],
    ticks: uniqueYAxis,
  };
};

const TotalMeetingsCost = ({ isOwn, filter, onError }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const totalMeetingsCost = useSelector(makeTotalMeetingsCost);
  const profile = useSelector(makeProfile);
  const dispatch = useDispatch();
  const { showModal } = useModal();

  if (totalMeetingsCost.error) {
    onError(totalMeetingsCost.error);
  }

  useEffect(() => {
    dispatch(
      getTotalMeetingsCostAsync({
        filter,
        isCostIndexPresentation: profile.isCostIndexPresentation,
      })
    );
  }, [dispatch, profile, filter]);

  const [getSubsequentColor] = useAvailableColors();

  const handleBarClick = ({ activeLabel }) => {
    const { data, timeFrame } = totalMeetingsCost;
    const selectedDay = data?.find(({ date }) => date === activeLabel) || {};

    const { dailyMeetings } = selectedDay;

    if (!dailyMeetings?.length) return;

    const { details, tooltip } = getTotalMeetingCostDetailedInformation(
      selectedDay,
      t
    );
    showModal({
      show: true,
      title: tooltip,
      component: (
        <MeetingDetailsContent
          meetings={dailyMeetings}
          details={details}
          timeFrame={timeFrame}
        />
      ),
      contentClass: "meeting-tooltip-details-content",
      footerButtons: [
        {
          icon: "close",
          text: t("components.Modal.FooterButtons.close"),
          onClick: () => Promise.resolve({}),
        },
        {
          icon: "reports",
          text: t("components.Modal.FooterButtons.goToReport"),
          onClick: () =>
            Promise.resolve({}).then(() => {
              navigate(Pages.ReportsMeetings);
              return {};
            }),
        },
      ],
    });
  };

  let availableYSettings = [
    {
      dataKey: "recurring",
      value: MeetingTypes.Recurring,
      color: getSubsequentColor(MeetingTypeColorIndexes.Recurring),
    },
    {
      dataKey: "unique",
      value: MeetingTypes.Unique,
      color: getSubsequentColor(MeetingTypeColorIndexes.Unique),
    },
    {
      dataKey: "external",
      value: MeetingTypes.External,
      color: getSubsequentColor(MeetingTypeColorIndexes.External),
    },
  ];
  if (filter.meetingType !== MeetingTypes.All) {
    availableYSettings =
      filter.meetingType === MeetingTypes.Internal
        ? availableYSettings.filter(
            (setting) => setting.value !== MeetingTypes.External
          )
        : availableYSettings.filter(
            (setting) => setting.value === filter.meetingType
          );
  }
  return (
    <ChartContainer
      title={
        isOwn
          ? t("components.Charts.ownTotalMeetingsCost")
          : t("components.Charts.teamTotalMeetingsCost")
      }
      isLoading={totalMeetingsCost.load}
      className="total-meetings-cost"
      renderTitleComponent={() => {
        return (
          totalMeetingsCost && (
            <div className="total-cost">
              {FormatHelper.getCurrency(totalMeetingsCost.amount)}
            </div>
          )
        );
      }}
      renderRightComponent={() => (
        <div className="legend">
          {availableYSettings.map((setting) => {
            return (
              <div key={setting.dataKey} className="legend-item">
                <span
                  style={{ background: setting.color }}
                  className="bullet"
                ></span>
                <span>
                  {t(`components.Charts.totalMeetingsCost.${setting.dataKey}`)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      data={totalMeetingsCost.data}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={totalMeetingsCost.data}
          margin={{
            top: 20,
            right: 0,
            left: 0,
            bottom: 5,
          }}
          maxBarSize={20}
          onClick={handleBarClick}
        >
          <CartesianGrid className="cartesian-grid" />
          <XAxis
            dataKey="date"
            axisLine={false}
            padding={{ left: 50, right: 70 }}
          />
          <YAxis
            axisLine={false}
            {...getUniqueYAxisProps(totalMeetingsCost.data)}
          />
          <Tooltip content={<CustomTooltip isSingleValue={false} />} cursor={{ className: 'custom-cursor' }} />
          {availableYSettings.map((setting) => (
            <Bar
              key={setting.dataKey}
              dataKey={setting.dataKey}
              stackId="a"
              fill={setting.color}
              shape={<CustomBarShape renderedSegments={availableYSettings.map(s => s.dataKey)} />}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

TotalMeetingsCost.propTypes = {
  isOwn: PropTypes.bool.isRequired,
  onError: PropTypes.func.isRequired,
  filter: filterPropTypes,
};

export default TotalMeetingsCost;
